import React, {
  useState,
  useContext,
  useEffect,
  MutableRefObject,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  createStyles,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
} from '@material-ui/core';
import { styled, Theme } from '@material-ui/core/styles';
import {
  AccountDetail,
  AccountDetailList,
  AccountDetailParams,
  Maybe,
  OutageLookUpType,
  OutageLookupMultipleOption,
  OutageLookupParams,
  OutageLookupResponse,
  ReportOutageSource,
} from '../../../../__generated__/pge-types';
import { useTranslation } from '../../../../hooks/useTranslation';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import useFormState from '../../../../hooks/useFormState';
import colors from '../../../../themes/main-colors';
import PhoneTextField from '../../../phone-text-field';
import {
  displayPhoneNumberFormat,
  parsePhoneNumber,
} from '../../../../util/format';
import validationRules, { Model } from './locateOutageAccount.rule';
import CallbackNotification from './CallbackNotification';
import { useOutageLookUp } from '../hooks/useOutageLookUp';
import { useReportOutageApi } from '../outage-map-panel/useOutageReportApi';
import { useIsMobile } from '../../../../util/style-utils';
import { moveCursorToEnd } from '../header/header';
import {
  NotificationRequestAccountParams,
  OutageDetailInfo,
} from '../../OutageCenterPage';
import useAnonymousNotifSubmission from '../outage-notifications-popup/useAnonymousNotifSubmission';
import useOutageCenterUtils from '../utils';
import { useOutageEvents } from '../../useOutageEvents';
import { setOutageReportedStatusForMedallia } from '../../../../lib/gtmService';
import { Shadow } from '../../../../constants';
import BulbIcon from '../../../../static/bulbIcon.svg';
import AccountTextField from '../../../account-text-field';
import BottomDrawerLayout, {
  DrawerSnapType,
} from '../bottom-drawer-layout/BottomDrawerLayout';
import ReportOutageIcon from '../reportOutageIcon/ReportOutageIcon';
import { FieldError } from '../../../../hooks/useFormState.types';
import useAuth from '../../../../hooks/useAuth';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useAuthQuery from '../../../../hooks/useAuthQuery';
import { getAccountDetails } from '../../../closed-accounts/queries';
import { Close } from '@material-ui/icons';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import AlertBanner from '../../../AlertBanner';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    boxWrapper: {
      marginTop: 10,
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
      padding: theme.spacing(2.5, 3, 2.75, 3),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        boxShadow: 'none',
        marginTop: 10,
        borderBottom: `1px solid ${colors.lightGray1}`,
      },
      backgroundColor: colors.white,
    },

    closeButton: {
      position: 'absolute',
      top: 6,
      right: 24,
    },
    title: {
      lineHeight: '2.25rem',
    },
    titleTextMob: {
      marginLeft: '1rem',
    },

    ReportOutageButton: {
      fontSize: '1rem',
      borderRadius: 5,
      color: colors.white,
      left: '50%',
      width: '168px',
      height: '38px',
      transform: 'translate(-50%)',
      background: colors.orange,
      letterSpacing: 0.32,
      boxShadow: `0px 5px 5px ${colors.boxShadowColor}`,
      '&:hover': {
        boxShadow: theme.shadows[5],
        background: colors.orange,
      },
      '&.MuiButton-root.Mui-disabled': {
        color: colors.quickSilverGrey,
        backgroundColor: colors.disableColor,
      },
    },
    ReportOutageButtonMob: {
      fontSize: '1.1rem',
      borderRadius: 18,
      color: colors.white,
      left: '50%',
      width: '90%',
      height: '42px',
      transform: 'translate(-50%)',
      background: colors.orange,
      letterSpacing: 0.32,
      marginTop: '2rem',
      '&:hover': {
        boxShadow: theme.shadows[5],
        background: colors.orange,
      },
      '&.MuiButton-root.Mui-disabled': {
        color: colors.quickSilverGrey,
        backgroundColor: colors.disableColor,
      },
    },
    buttonContainer: {
      display: 'flex',
      padding: '1rem 1rem 0rem 1rem',
      width: '100%',
      justifyContent: 'space-evenly',
    },
    backButton: {
      flex: 1,
    },
    subButtonContainer: {
      flex: '0',
    },
    reportOutageAccInfo: {
      background: colors.white,
      width: '100%',
      margin: '0px',
    },

    reportOutageContentStyle: {
      display: 'flex',
      flexDirection: 'column',
      boxShadow: Shadow.ONE,
    },
    reportOutageHeaderStyle: {
      display: 'flex',
      flexDirection: 'row',
      marginBottom: '1rem',
    },
    switchDetailsStyle: {
      display: 'flex',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    switchDetailsStyleMob: {
      display: 'flex',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      marginLeft: 'auto',
      marginBottom: '1rem',
    },
    modalBody: {},
    kickerText: {
      paddingBottom: theme.spacing(1.875),
      paddingTop: theme.spacing(1.25),
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
    bodyText: {
      fontSize: '1rem',
      lineHeight: '1.125rem',
    },
    lowerCase: {
      textTransform: 'lowercase',
    },
    reportOutageAccInfoInner: {
      padding: '15px',
    },
    lookupByPhoneOrAcc: {
      display: 'flex',
      flexDirection: 'row',
    },
    outageNotificationInfo: {
      display: 'flex',
      width: '65%',
    },
    reconnectPopupStyles: {
      width: 'auto',
    },
    reconnectPopupStylesMobile: {
      width: '100%',
      marginTop: '1rem',
    },
    outageNotificationInfoMobile: {
      display: 'flex',
      width: '100%',
      marginTop: '1rem',
    },
    notificationMessage: {
      margin: '0 8px',
    },
    phoneNumberCheckBox: {
      flexDirection: 'row',
      background: '#FFFCD9',
      display: 'flex',
      alignItems: 'center',
      padding: '0px 12px',
      height: '10vh',
      maxHeight: '10vh',
    },
    phoneNumberCheckBoxMobile: {
      flexDirection: 'column',
      background: '#FFFCD9',
      display: 'flex',
      alignItems: 'center',
      padding: '0px 12px',
      height: 'auto',
    },
    ReportOutageTitleMob: {
      display: 'flex',
      alignItems: 'center',
    },
    ReportOutageImage: {
      width: '95%',
    },
    ReportOutageImageMob: {
      height: '200px',
      width: '100%',
    },
    OutageBackButton: {
      textDecoration: 'underline',
      color: colors.sparkBlue,
    },
    OutageBackButtonMob: {
      textDecoration: 'underline',
      color: colors.sparkBlue,
      width: '100%',
    },
    dividerDetails: {
      padding: '1px',
      backgroundColor: colors.black,
    },
    unauthenticatedUserOutageAccordion: {
      background: '#F2F2F2',
      margin: '0px !important',
      boxShadow: 'none',
      '.MuiAccordionSummary-content': {
        margin: '0px',
      },
    },
    unauthenticatedUserAccordionDetails: {
      flexDirection: 'column',
      display: 'flex',
    },
    RenderAccountInfoMob: {
      marginLeft: '1rem',
      marginBottom: '1rem',
    },
    ReconnectAnonymousMessageHeader: {
      marginBottom: '1rem',
    },
    ReconnectAnonymousMessage: {
      background: colors.navBarBackground,
      padding: '0rem 1rem',
      boxShadow: Shadow.ONE,
      overflow: 'auto',
      marginTop: '1rem',
    },
    ReconnectAnonymousMessageMobile: {
      textAlign: 'center',
    },
    buttonContainerReconnectMessage: {
      display: 'flex',
      padding: '1rem',
      width: '100%',
      justifyContent: 'space-evenly',
      alignItems: 'baseline',
    },
    buttonContainerReconnectMessageMobile: {
      display: 'flex',
      flexDirection: 'column-reverse',
      alignItems: 'center',
      justifyContent: 'center',
      alignContent: 'center',
    },
    cancelButton: {
      width: '17%',
      marginTop: '16px',
    },
    contactButton: {
      width: '13%',
    },
    reconnectAnonymousCloseButton: {
      width: '120px',
      color: colors.sparkBlue,
    },
    selectLocationText: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 600,
      color: colors.darkBlue,
    },
    radioButtonIcon: {
      '& .MuiSvgIcon-root': {
        fontSize: '1rem',
      },
    },
    safetyPriorityCallButton: {
      fontSize: '1rem',
      borderRadius: 5,
      color: colors.white,
      width: '130px',
      background: colors.orange,
      letterSpacing: 0.32,
      boxShadow: `0px 5px 5px ${colors.boxShadowColor}`,
      '&:hover': {
        boxShadow: theme.shadows[5],
        background: colors.orange,
      },
    },
  }),
);

const defaultFormValues: Model = {
  lookUpType: undefined,
  lookUpPhoneNumber: '',
  lookUpAccountNumber: '',
};
export interface Props {
  premiseInfo?: string;
  accontNumber: string;
  name: string;
  primaryContactNumber: string;
  onClose: () => void;
  reportInfo?: (lookupinfo: OutageDetailInfo) => void;
  isOtherLocation: boolean;
  setShowReconnectPopup?: (value: boolean) => void;
  onPrevious?: (addressNumber?: string, showResetInfo?: boolean) => void;
  outageLookUpResult: (
    data: OutageLookupResponse,
    isMultiPremise?: boolean,
    showSuccess?: boolean,
    showConnectionInfo?: boolean,
  ) => void;
  reportOutageForLoggedIn: () => void;
  setHouseNumber?: (houseNumber: string) => void;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
  isFindAccount?: boolean;
  test?: () => void;
}
type OutageStatusWarnings = {
  showConnectionInfo: boolean;
  showResetInfo: boolean;
};
type GetLocationDetailsReturnType =
  | {
      encryptedServicePointIdList?: any[];
      encryptedPremiseId?: string;
      encryptedAccountNumber?: string;
      houseNumber?: string;
      outageStatusWarnings?: OutageStatusWarnings;
      multiplePremiseList?: Maybe<Array<OutageLookupMultipleOption>>;
      lookupResponse?: OutageLookupResponse;
    }
  | undefined;

export default function OutageLocationLookup(props: Props) {
  const {
    name,
    accontNumber,
    premiseInfo,
    onPrevious,
    onClose,
    reportInfo,
    isOtherLocation,
    primaryContactNumber,
    setShowReconnectPopup,
    reportOutageForLoggedIn,
  } = props;
  const classes = useStyles();
  const { t, richT } = useTranslation();
  const isMobile = useIsMobile();
  const { setState: updateTransition } = useContext(TransitionContext);
  const { setOutageMapNotification } = useOutageEvents();
  const [channelType, setChannelType] = useState<string>('Phone');
  const alternateLookupForm = useFormState(defaultFormValues, {
    validate: validationRules,
  });

  const { getOutageLookUpQueryResponse } = useOutageLookUp();
  const { reportOutageForGuest } = useReportOutageApi();

  const [callBackPhoneFieldError, setCallBackPhoneFieldError] = useState(false);
  const [multiplePremiseInfo, setMultiplePremiseInfo] = useState<Maybe<
    Array<OutageLookupMultipleOption>
  > | null>(null);
  const [
    selectedMultiPremiseEncryptedValue,
    setSelectedNultiPremiseEncryptedValue,
  ] = useState<string | null>(null);
  const [callBackPhoneFieldChecked, setCallBackPhoneFieldChecked] = useState(
    false,
  );
  const [callBackPhoneFieldValue, setCallBackPhoneFieldValue] = useState('');
  const [callBackPhoneChecked, setCallBackPhoneChecked] = useState(false);

  const [outageDetails, setOutageDetails] = useState<
    OutageLookupResponse | undefined
  >();

  const { OMSdowntime } = useContext(featureFlagsContext);

  const [encryptedPremiseId, setEncryptedPremiseId] = useState<
    string | undefined
  >();
  const inputField = React.useRef();
  const customerCareNumber = t('CUSTOMER_CARE_NUMBER_OTHER');
  const { loading, data } = useAuthQuery<
    {
      getAccountDetails: Array<AccountDetail>;
      getAccountDetailList: AccountDetailList;
    },
    {
      params: AccountDetailParams;
    }
  >(getAccountDetails, {
    variables: {
      params: { accountNumberList: [] },
    },
    skip: [].length === 0,
  });

  const { form, handleOnSubmit } = useAnonymousNotifSubmission({
    outageData: {
      isEligibleToReport: true,
    },
    isReported: true,
    isNotificationSubmit: false,
    isOutageByZipCode: false,
    isOutageByEvent: false,
    isOutageByLookup: false,
    isOutageByAccount: false,
    shouldRefetchAccount: false,
    selectedCountyName: null,
    selectedZipCode: null,
    selectedZipIndex: null,
    zipCodes: null,
  });
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (callBackPhoneFieldValue === '') {
      setCallBackPhoneFieldValue(primaryContactNumber);
    }
  }, [primaryContactNumber]);
  const { setErrorNotification } = useOutageCenterUtils();

  const hasMultipleAddresses =
    outageDetails?.multiplePremiseList !== null &&
    outageDetails?.multiplePremiseList !== undefined &&
    outageDetails?.multiplePremiseList?.length > 1;

  const isCallBackPhoneFieldInvalid =
    callBackPhoneFieldChecked &&
    (callBackPhoneFieldError || callBackPhoneFieldValue.length !== 10);

  const handleCallbackNotificationChange = (
    checked: boolean,
    phone: string,
    error: any,
  ) => {
    setCallBackPhoneFieldError(error);
    setCallBackPhoneFieldValue(phone);
    setCallBackPhoneFieldChecked(checked);
  };

  const onPasteHandle = async (
    lookUpTypeSelection: OutageLookUpType,
    e: any,
  ) => {
    const value = e.clipboardData.getData('text/plain');
    if (lookUpTypeSelection === OutageLookUpType.Phone) {
      await alternateLookupForm.setValue(
        'lookUpPhoneNumber',
        parsePhoneNumber(value),
      );
    }

    if (lookUpTypeSelection === OutageLookUpType.Account) {
      await alternateLookupForm.setValue('lookUpAccountNumber', value);
    }
    moveCursorToEnd(inputField);
  };

  const onCopyHandle = (e: any) => {
    e.clipboardData.setData('text/plain', e.target.value);
  };

  const onCutHandle = (e: any) => {
    e.clipboardData.setData('text/plain', e.target.value);
  };

  const onChangeInputHandle = async (
    lookUpTypeSelection: OutageLookUpType,
    e: any,
  ) => {
    if (lookUpTypeSelection === OutageLookUpType.Phone) {
      await alternateLookupForm.setValue(
        'lookUpPhoneNumber',
        parsePhoneNumber(e.target.value),
      );
    }

    if (lookUpTypeSelection === OutageLookUpType.Account) {
      await alternateLookupForm.setValue('lookUpAccountNumber', e.target.value);
    }
    await alternateLookupForm.setValue('lookUpType', lookUpTypeSelection);
    moveCursorToEnd(inputField);
  };

  const handleLocationNotFound = async () => {
    const errorMessage = `${t('ACCOUNT_NOT_FOUND')} ${t(
      'CALL_FOR_ASSISTANCE',
    )} ${t('RESIDENTIAL_CUSTOMER_SERVICE_NUMBER')} ${t('BETWEEN_THE_HOURS')}`;

    alternateLookupForm.values.lookUpType === OutageLookUpType.Phone
      ? alternateLookupForm.setError('lookUpPhoneNumber', errorMessage)
      : alternateLookupForm.setError('lookUpAccountNumber', errorMessage);
  };

  const closeAndShowError = () => {
    setErrorNotification(true);
    updateTransition({ open: false });
    props.onPrevious && props.onPrevious('');
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const getLocationDetails = async (
    params: OutageLookupParams,
  ): Promise<GetLocationDetailsReturnType> => {
    try {
      setOutageDetails(undefined);
      setEncryptedPremiseId(undefined);
      updateTransition({ open: true });
      const result = await getOutageLookUpQueryResponse(params);
      if (
        Boolean(result) &&
        result?.isEligibleToReport !== null &&
        result?.isEligibleToReport !== undefined
      ) {
        const isMultiAddress =
          result?.multiplePremiseList !== null &&
          result?.multiplePremiseList !== undefined &&
          result?.multiplePremiseList?.length > 1;

        if (
          !result?.isAccountExists ||
          (!result?.isEligibleToReport && !isMultiAddress)
        ) {
          await handleLocationNotFound();
          updateTransition({ open: false });
          return;
        }
        setOutageDetails(result);

        result?.encryptedPremiseId &&
          setEncryptedPremiseId(result?.encryptedPremiseId);

        const encryptedServicePointIdList = result?.outages?.map(
          (service: any) => service?.encryptedServicePointId,
        );

        const isReadyToSubmit =
          !isMultiAddress && Boolean(encryptedServicePointIdList);

        updateTransition({ open: false });
        return {
          encryptedServicePointIdList: isReadyToSubmit
            ? encryptedServicePointIdList
            : [],
          encryptedPremiseId: result?.encryptedPremiseId!,
          encryptedAccountNumber: result?.encryptedAccountNumber!,
          houseNumber: result?.houseNumber as string,
          outageStatusWarnings: result?.outageStatusWarnings as OutageStatusWarnings,
          multiplePremiseList: result?.multiplePremiseList,
          lookupResponse: result,
        };
      } else {
        closeAndShowError();
      }
    } catch {
      closeAndShowError();
    }
  };

  const getLocationDetailsBySearchInput = async (): Promise<GetLocationDetailsReturnType> => {
    if (!alternateLookupForm.values.lookUpType) {
      //closeAndShowError();
      //to-do when working on multiple premise
    }
    const params: OutageLookupParams = {
      lookupType:
        !isOtherLocation && isAuthenticated
          ? OutageLookUpType.Account
          : alternateLookupForm?.values?.lookUpType!,
      value:
        !isOtherLocation && isAuthenticated
          ? accontNumber
          : alternateLookupForm.values.lookUpType === OutageLookUpType.Phone
          ? displayPhoneNumberFormat(
              alternateLookupForm?.values?.lookUpPhoneNumber,
            )
          : alternateLookupForm?.values?.lookUpAccountNumber,
    };
    updateTransition({ open: true });
    try {
      let locationResponse: GetLocationDetailsReturnType = undefined;
      await alternateLookupForm.submit(
        async () => {
          updateTransition({ open: false });
          locationResponse = await getLocationDetails(params);
        },
        () => {
          updateTransition({ open: false });
        },
      )();

      return locationResponse;
    } catch {
      closeAndShowError();
    }
  };

  const reportOutage = async (
    encryptedServicePointIdList?: any[],
    encryptedPremiseIdAsParams?: string,
    encryptedAccountNumber?: string,
  ) => {
    if (!encryptedServicePointIdList) {
      closeAndShowError();
    }
    updateTransition({ open: true });
    try {
      const outageResponse =
        encryptedAccountNumber &&
        (await reportOutageForGuest({
          encryptedServicePointList: encryptedServicePointIdList!,
          callbackPhone: callBackPhoneChecked ? callBackPhoneFieldValue : '',

          source: ReportOutageSource.Web,
          encryptedAccountNumber: encryptedAccountNumber,
        }));
      if (
        outageResponse &&
        outageResponse?.reportOutageBySpListGuest?.success
      ) {
        await fetchingLatestPremiseInfo(encryptedPremiseIdAsParams);
      } else {
        closeAndShowError();
      }
    } catch {
      closeAndShowError();
    }
  };

  const fetchingLatestPremiseInfo = async (
    encryptedPremiseIdAsParams: string | undefined,
  ) => {
    const params = {
      lookupType: OutageLookUpType.Premise,
      encryptedValue: encryptedPremiseIdAsParams,
    };
    try {
      const result = await getOutageLookUpQueryResponse(params);
      if (
        Boolean(result) &&
        result?.isEligibleToReport !== null &&
        result?.isEligibleToReport !== undefined
      ) {
        props.outageLookUpResult(result);
        updateTransition({ open: false });
      } else {
        closeAndShowError();
      }
    } catch {
      closeAndShowError();
    }
  };
  const guestReportOutage = async (
    locationResponse: GetLocationDetailsReturnType,
  ) => {
    locationResponse?.encryptedAccountNumber &&
      (await reportOutageForGuest({
        encryptedServicePointList: locationResponse?.encryptedServicePointIdList!,
        callbackPhone: callBackPhoneFieldValue,

        source: ReportOutageSource.Web,
        encryptedAccountNumber: locationResponse?.encryptedAccountNumber,
      }));
  };
  const checkConnectionInfo = async (
    locationResponse: GetLocationDetailsReturnType,
  ) => {
    if (!locationResponse) {
      return;
    }
    if (!isAuthenticated || isOtherLocation) {
      const showResetInfo =
        locationResponse?.outageStatusWarnings?.showResetInfo;
      const showConnectionInfo =
        locationResponse?.outageStatusWarnings?.showConnectionInfo;
      if (showResetInfo) {
        if (showConnectionInfo) {
          updateTransition({ open: true });
          const response = guestReportOutage(locationResponse);
          updateTransition({ open: false });

          setShowReconnectPopup && setShowReconnectPopup(true);
        } else {
          props.onPrevious &&
            props.onPrevious(
              locationResponse?.houseNumber,
              Boolean(
                locationResponse?.lookupResponse?.outageStatusWarnings
                  ?.showResetInfo,
              ),
            );

          reportInfo &&
            reportInfo({
              encryptedServicePointList: locationResponse?.encryptedServicePointIdList!,
              callbackPhone: callBackPhoneFieldValue,
              encryptedAccountNumber:
                locationResponse?.encryptedAccountNumber || '',
              encryptedPremiseId: locationResponse?.encryptedPremiseId || '',
            });
        }
      } else {
        updateTransition({ open: true });
        const response = guestReportOutage(locationResponse);
        updateTransition({ open: false });

        props.setHouseNumber &&
          props.setHouseNumber(locationResponse?.houseNumber || '');
      }
    } else {
      // If a single, eligible premise is located,
      // then report the outage:
      locationResponse?.encryptedPremiseId &&
        locationResponse?.encryptedServicePointIdList &&
        locationResponse?.encryptedAccountNumber &&
        reportOutage(
          locationResponse.encryptedServicePointIdList,
          locationResponse.encryptedPremiseId,
          locationResponse.encryptedAccountNumber,
        );
    }
  };
  const getUpdatedMultiPremiseList = (
    premiseList: OutageLookupMultipleOption[],
  ) => {
    if (premiseList) {
      const updatedPremiseList = [];
      for (const mainPremise of premiseList) {
        let count = 0;
        for (const subPremise of premiseList) {
          if (mainPremise.houseNumber === subPremise.houseNumber) {
            count = count + 1;
          }
        }
        if (count > 1) {
          updatedPremiseList.push({
            ...mainPremise,
            houseNumber: `${mainPremise.houseNumber} - ${mainPremise.last4CharAddress}`,
          });
        } else {
          updatedPremiseList.push({ ...mainPremise });
        }
        count = 0;
      }
      return updatedPremiseList;
    }
    return null;
  };
  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    // Resolved multi-prem and submitted:
    if (Boolean(encryptedPremiseId)) {
      const response = await getLocationDetails({
        lookupType: OutageLookUpType.Premise,
        encryptedValue: encryptedPremiseId,
      });
      response?.encryptedServicePointIdList &&
        response?.encryptedAccountNumber &&
        (await reportOutage(
          response?.encryptedServicePointIdList,
          encryptedPremiseId,
          response?.encryptedAccountNumber,
        ));
      return;
    }

    if (multiplePremiseInfo && selectedMultiPremiseEncryptedValue) {
      const params = {
        lookupType: OutageLookUpType.Premise,
        encryptedValue: selectedMultiPremiseEncryptedValue,
      };
      const locationResponse = await getLocationDetails(params);

      // if (
      //   locationResponse?.lookupResponse?.outages?.find(
      //     outage => outage?.hasKnownOutage,
      //   )
      // ) {
      const isMultiPremise = Boolean(
        locationResponse?.lookupResponse?.multiplePremiseList?.length,
      );
      props.outageLookUpResult(
        locationResponse?.lookupResponse!,
        isMultiPremise,
        false,
      );
      onClose();
      return;
      // }

      // if (locationResponse?.encryptedAccountNumber) {
      //   const showResetInfo =
      //     locationResponse?.outageStatusWarnings?.showResetInfo;
      //   const showConnectionInfo =
      //     locationResponse?.outageStatusWarnings?.showConnectionInfo;
      //   if (showResetInfo) {
      //     if (showConnectionInfo) {
      //       setShowReconnectPopup && setShowReconnectPopup(true);
      //     } else {
      //       if (locationResponse?.lookupResponse) {
      //         props.outageLookUpResult(
      //           locationResponse.lookupResponse,
      //           false,
      //           false,
      //         );
      //         updateTransition({ open: false });
      //       }
      //       props.onPrevious && props.onPrevious(locationResponse?.houseNumber);
      //       reportInfo &&
      //         reportInfo({
      //           encryptedServicePointList: locationResponse?.encryptedServicePointIdList!,
      //           callbackPhone: callBackPhoneFieldValue,
      //           encryptedAccountNumber:
      //             locationResponse?.encryptedAccountNumber || '',
      //           encryptedPremiseId: locationResponse?.encryptedPremiseId || '',
      //         });
      //     }
      //   } else {
      //     const locationResponseForGuest = await reportOutageForGuest({
      //       encryptedServicePointList: locationResponse?.encryptedServicePointIdList!,
      //       callbackPhone: callBackPhoneFieldValue,

      //       source: ReportOutageSource.Web,
      //       encryptedAccountNumber: locationResponse?.encryptedAccountNumber,
      //     });
      //     if (
      //       locationResponseForGuest &&
      //       locationResponseForGuest?.reportOutageBySpListGuest?.success
      //     ) {
      //       await fetchingLatestPremiseInfo(
      //         locationResponse?.encryptedPremiseId,
      //       );
      //     } else {
      //       closeAndShowError();
      //     }
      //     props.setHouseNumber &&
      //       props.setHouseNumber(locationResponse?.houseNumber || '');
      //   }
      // }
    } else {
      if (isAuthenticated && !isOtherLocation) {
        reportOutageForLoggedIn();
        onClose();
      } else {
        //Initial look up by phone or account number.
        const locationResponse = await getLocationDetailsBySearchInput();
        if (locationResponse) {
          if (locationResponse?.multiplePremiseList) {
            setMultiplePremiseInfo(
              getUpdatedMultiPremiseList(locationResponse?.multiplePremiseList),
            );
          } else {
            setMultiplePremiseInfo(null);
            setSelectedNultiPremiseEncryptedValue(null);

            // if (
            //   locationResponse?.lookupResponse?.outages?.find(
            //     outage => outage?.hasKnownOutage,
            //   )
            // ) {
            const isMultiPremise = Boolean(
              locationResponse?.lookupResponse?.multiplePremiseList?.length,
            );
            props.outageLookUpResult(
              locationResponse?.lookupResponse!,
              isMultiPremise,
              false,
              locationResponse?.outageStatusWarnings?.showConnectionInfo,
            );
            onClose();
            // } else {
            //   await checkConnectionInfo(locationResponse);
            // }
          }
        }
      }
    }
    //
  };

  const handleMultiPremiseSelect = async (selectedPremise: string) => {
    setSelectedNultiPremiseEncryptedValue(selectedPremise);
  };

  const handleSubmitMobile = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (props.isFindAccount) {
      await handleFindAccountSubmit();
      return;
    }

    const params: any = {
      lookupType: alternateLookupForm.values.lookUpType,
      value:
        alternateLookupForm.values.lookUpType === OutageLookUpType.Phone
          ? displayPhoneNumberFormat(
              alternateLookupForm.values.lookUpPhoneNumber,
            )
          : alternateLookupForm.values.lookUpAccountNumber,
    };

    updateTransition({ open: true });
    try {
      const getOutageLookupResponse = await getOutageLookUpQueryResponse(
        params,
      );
      if (
        getOutageLookupResponse &&
        getOutageLookupResponse?.isEligibleToReport !== null &&
        getOutageLookupResponse?.isEligibleToReport !== undefined
      ) {
        props.outageLookUpResult(getOutageLookupResponse);
        const isMultiPremise = Boolean(
          isMobile &&
            getOutageLookupResponse.multiplePremiseList &&
            getOutageLookupResponse.multiplePremiseList.length > 1,
        );
        props.onPrevious && props.onPrevious(''); //To close the main account panel when multi premise popup is opened - Mobile
      } else {
        closeAndShowError();
      }
      updateTransition({ open: false });
    } catch {
      closeAndShowError();
    }
  };

  const handleFindAccountSubmit = async () => {
    setOutageDetails(undefined);

    const params: any = {
      lookupType: Boolean(encryptedPremiseId)
        ? OutageLookUpType.Premise
        : alternateLookupForm.values.lookUpType,
      value: Boolean(encryptedPremiseId)
        ? null
        : alternateLookupForm.values.lookUpType === OutageLookUpType.Phone
        ? displayPhoneNumberFormat(alternateLookupForm.values.lookUpPhoneNumber)
        : alternateLookupForm.values.lookUpAccountNumber,
      encryptedValue: Boolean(encryptedPremiseId) ? encryptedPremiseId : null,
    };

    updateTransition({ open: true });
    try {
      const getOutageLookupResponse = await getOutageLookUpQueryResponse(
        params,
      );
      if (
        getOutageLookupResponse &&
        getOutageLookupResponse?.isEligibleToReport !== null &&
        getOutageLookupResponse?.isEligibleToReport !== undefined
      ) {
        const isMultiPremise = Boolean(
          getOutageLookupResponse.multiplePremiseList &&
            getOutageLookupResponse.multiplePremiseList.length > 1,
        );

        if (
          !getOutageLookupResponse?.isAccountExists ||
          (!getOutageLookupResponse?.isEligibleToReport && !isMultiPremise)
        ) {
          await handleLocationNotFound();
          updateTransition({ open: false });
          return;
        }
        if (!isMobile && isMultiPremise) {
          setOutageDetails(getOutageLookupResponse);
          updateTransition({ open: false });
          return;
        }
        if (
          callBackPhoneFieldChecked &&
          !isCallBackPhoneFieldInvalid &&
          !isMultiPremise &&
          getOutageLookupResponse?.outages &&
          getOutageLookupResponse?.outages[0]?.hasKnownOutage
        ) {
          form.values.callbackPhoneNumber = callBackPhoneFieldValue;
          await handleOnSubmit(
            getOutageLookupResponse?.encryptedAccountNumber &&
              getOutageLookupResponse?.encryptedAccountNumber,
            getOutageLookupResponse?.outages &&
              getOutageLookupResponse?.outages[0].encryptedServicePointId,
          );
        }
        props.outageLookUpResult(getOutageLookupResponse, isMultiPremise);
        props.onPrevious && props.onPrevious(''); //To close the main account panel when multi premise popup is opened - Mobile
      } else {
        closeAndShowError();
      }
      updateTransition({ open: false });
    } catch {
      closeAndShowError();
    }
  };
  const onConfirmPopup = () => {
    props.onClose();
    // props.setLoggedInUserNotificationRequestAccountParams({
    //   accountNumber: '',
    //   encryptedPersonId: '',
    // });
    //work to do
    setOutageMapNotification('success', 'REPORT_SUCCESSFUL');
    setOutageReportedStatusForMedallia(1);
  };

  const reportedOutageButtonCheck = () => {
    if (isAuthenticated && !isOtherLocation && accontNumber !== '') {
      return false;
    }
    if (multiplePremiseInfo && !selectedMultiPremiseEncryptedValue) {
      return true;
    }
    return !(
      (alternateLookupForm.values.lookUpType === OutageLookUpType.Account &&
        alternateLookupForm.values.lookUpAccountNumber !== '') ||
      (alternateLookupForm.values.lookUpType === OutageLookUpType.Phone &&
        alternateLookupForm.values.lookUpPhoneNumber !== '')
    );
  };
  return (
    <>
      {!isMobile ? (
        <>
          <div className={classes.reportOutageHeaderStyle}>
            <div style={{ flex: '2' }}>
              <Typography className={classes.title} variant={'h2'}>
                {t('REPORT_OUTAGE_TITLE')}
              </Typography>
            </div>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={onClose}
              data-testid="close-icon"
              style={{
                padding: '0px !important',
              }}
            >
              <Close />
            </IconButton>
          </div>
          {OMSdowntime ? (
            <Grid item>
              <AlertBanner
                icon={false}
                title={t('OMS_DOWN_TIME_TITLE')}
                text={richT('OMS_DOWN_TIME_OUTAGE_MAP')}
              />
            </Grid>
          ) : (
            <>
              <div className={classes.reportOutageContentStyle}>
                {!isAuthenticated && (
                  <Accordion
                    className={classes.unauthenticatedUserOutageAccordion}
                  >
                    <AccordionSummary
                      style={{ margin: '0' }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography variant={'subtitle2'}>
                        {richT('DO_YOU_HAVE_DIM_OR_FLICKERING_LIGHTS')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      className={classes.unauthenticatedUserAccordionDetails}
                    >
                      <Divider
                        variant="middle"
                        className={classes.dividerDetails}
                      />
                      <Typography
                        variant={'h3'}
                        style={{ marginBottom: '0px' }}
                      >
                        {richT('YOUR_SAFETY_IS_OUR_TOP_PRIORITY')}
                      </Typography>
                      <Typography variant={'subtitle2'}>
                        {richT('DIM_OR_FLICKERING_LIGHTS_UNAUTHENTICATED_USER')}
                        {
                          <a
                            href={'tel:' + customerCareNumber}
                            style={{
                              textDecoration: 'underline',
                              color: colors.brightBlue,
                            }}
                          >
                            {' '}
                            {customerCareNumber}
                          </a>
                        }
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
                <Grid
                  className={classes.reportOutageAccInfo}
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={5}
                    className={classes.reportOutageAccInfoInner}
                  >
                    <img
                      className={classes.ReportOutageImage}
                      src={require('../../../../images/reportOutage_desktop.png')}
                      alt="ReportOutageImage"
                    />
                  </Grid>
                  {!isOtherLocation &&
                  isAuthenticated &&
                  accontNumber !== '' ? (
                    <Grid
                      item
                      xs={7}
                      style={{
                        margin: 'auto',
                      }}
                    >
                      <RenderAccountInfo
                        accountNumber={accontNumber}
                        name={name}
                        premiseInfo={premiseInfo}
                      />
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={7}
                      style={{
                        margin: 'auto',
                      }}
                    >
                      <Typography
                        variant={'subtitle1'}
                        style={{ fontWeight: 600 }}
                      >
                        {t('LOOKUP_BY_PHONE_OR_ACCOUNT')}
                      </Typography>
                      <div className={classes.lookupByPhoneOrAcc}>
                        <ChannelOptions
                          channelType={channelType}
                          setChannelType={async (type: string) => {
                            setMultiplePremiseInfo(null);
                            setSelectedNultiPremiseEncryptedValue(null);
                            await alternateLookupForm.setValue(
                              'lookUpType',
                              type === 'AccNo' ? 'ACCOUNT' : type.toUpperCase(),
                            );
                            setChannelType(type);
                          }}
                        />

                        {channelType === 'Phone' ? (
                          <PhoneField
                            fieldRef={inputField}
                            error={
                              Boolean(
                                alternateLookupForm.errors.lookUpPhoneNumber,
                              ) &&
                              alternateLookupForm.values.lookUpType ===
                                OutageLookUpType.Phone
                            }
                            helpertext={
                              alternateLookupForm.values.lookUpType ===
                                OutageLookUpType.Phone &&
                              alternateLookupForm.errors.lookUpPhoneNumber
                            }
                            value={
                              alternateLookupForm.values.lookUpType ===
                                OutageLookUpType.Phone &&
                              Boolean(
                                alternateLookupForm.values.lookUpPhoneNumber,
                              )
                                ? alternateLookupForm.values.lookUpPhoneNumber
                                : ''
                            }
                            onChange={async (phoneEvent: any) => {
                              await onChangeInputHandle(
                                OutageLookUpType.Phone,
                                phoneEvent,
                              );
                            }}
                          />
                        ) : (
                          <AccountField
                            fieldRef={inputField}
                            error={
                              alternateLookupForm.values.lookUpType ===
                                OutageLookUpType.Account &&
                              Boolean(
                                alternateLookupForm.errors.lookUpAccountNumber,
                              )
                            }
                            helpertext={
                              alternateLookupForm.values.lookUpType ===
                                OutageLookUpType.Account &&
                              alternateLookupForm.errors.lookUpAccountNumber
                            }
                            value={
                              alternateLookupForm.values.lookUpType ===
                                OutageLookUpType.Account &&
                              Boolean(
                                alternateLookupForm.values.lookUpAccountNumber,
                              )
                                ? alternateLookupForm.values.lookUpAccountNumber
                                : ''
                            }
                            onChange={async (accountEvent: any) => {
                              await onChangeInputHandle(
                                OutageLookUpType.Account,
                                accountEvent,
                              );
                            }}
                          />
                        )}
                      </div>
                    </Grid>
                  )}
                  {multiplePremiseInfo && (
                    <div style={{ marginLeft: '1rem' }}>
                      <MultiPremise
                        premiseList={multiplePremiseInfo}
                        onSelectPremise={handleMultiPremiseSelect}
                      />
                    </div>
                  )}
                </Grid>

                {isAuthenticated && !isOtherLocation && (
                  <div className={classes.phoneNumberCheckBox}>
                    <OutageNotificationInfo isOtherLocation={isOtherLocation} />
                    <div>
                      <CallbackNotification
                        onChange={(
                          checked: boolean,
                          phone: string,
                          error: any,
                        ) => {
                          setCallBackPhoneChecked(checked);
                          if (phone && phone !== '') {
                            setCallBackPhoneFieldValue(phone);
                          }
                        }}
                        primaryContactNumber={primaryContactNumber}
                        isFindAccount={true}
                      />
                    </div>
                  </div>
                )}
              </div>
              <Box className={classes.buttonContainer}>
                <BackButtonComponent
                  onPrevious={
                    isAuthenticated && !isOtherLocation ? onPrevious : onClose
                  }
                />
                <OutageButtonComponent
                  handleSubmit={handleSubmit}
                  disabled={reportedOutageButtonCheck()}
                />
              </Box>
            </>
          )}
        </>
      ) : (
        <BottomDrawerLayout
          data-testid={'notification-options'}
          refId={0}
          defaultSnap={DrawerSnapType.default}
        >
          <div>
            <div className={classes.ReportOutageTitleMob}>
              <ReportOutageIcon
                title={''}
                xOffset={'0'}
                width="45"
                height="45"
              />
              <Typography className={classes.titleTextMob} variant={'h2'}>
                {t('REPORT_OUTAGE_TITLE')}
              </Typography>
            </div>
            {OMSdowntime ? (
              <Grid item>
                <AlertBanner
                  icon={false}
                  title={t('OMS_DOWN_TIME_TITLE')}
                  text={richT('OMS_DOWN_TIME_OUTAGE_MAP')}
                />
              </Grid>
            ) : (
              <>
                {!isAuthenticated && (
                  <Accordion
                    className={classes.unauthenticatedUserOutageAccordion}
                  >
                    <AccordionSummary
                      style={{ margin: '0' }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography variant={'subtitle2'}>
                        {richT('DO_YOU_HAVE_DIM_OR_FLICKERING_LIGHTS')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      className={classes.unauthenticatedUserAccordionDetails}
                    >
                      <Divider
                        variant="middle"
                        className={classes.dividerDetails}
                      />
                      <Typography
                        variant={'h3'}
                        style={{ marginBottom: '0px' }}
                      >
                        {richT('YOUR_SAFETY_IS_OUR_TOP_PRIORITY')}
                      </Typography>
                      <Typography variant={'subtitle2'}>
                        {richT('DIM_OR_FLICKERING_LIGHTS_UNAUTHENTICATED_USER')}
                        {
                          <a
                            href={'tel:' + customerCareNumber}
                            style={{
                              textDecoration: 'underline',
                              color: colors.brightBlue,
                            }}
                          >
                            {' '}
                            {customerCareNumber}
                          </a>
                        }
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
                <img
                  className={classes.ReportOutageImageMob}
                  src={require('../../../../images/reportOutage_desktop.png')}
                  alt="ReportOutageImageMob"
                />
                {!isOtherLocation && isAuthenticated ? (
                  <RenderAccountInfo
                    accountNumber={accontNumber}
                    name={name}
                    premiseInfo={premiseInfo}
                  />
                ) : (
                  <Grid
                    item
                    xs={7}
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      maxWidth: '100%',
                      margin: '15px 0px',
                    }}
                  >
                    <Typography
                      variant={'subtitle1'}
                      style={{ fontWeight: 600 }}
                    >
                      {t('LOOKUP_BY_PHONE_OR_ACCOUNT')}
                    </Typography>
                    <div className={classes.lookupByPhoneOrAcc}>
                      <ChannelOptions
                        channelType={channelType}
                        setChannelType={setChannelType}
                      />

                      {channelType === 'Phone' ? (
                        <PhoneField
                          fieldRef={inputField}
                          error={
                            Boolean(
                              alternateLookupForm.errors.lookUpPhoneNumber,
                            ) &&
                            alternateLookupForm.values.lookUpType ===
                              OutageLookUpType.Phone
                          }
                          helpertext={
                            alternateLookupForm.values.lookUpType ===
                              OutageLookUpType.Phone &&
                            alternateLookupForm.errors.lookUpPhoneNumber
                          }
                          value={
                            alternateLookupForm.values.lookUpType ===
                              OutageLookUpType.Phone &&
                            Boolean(
                              alternateLookupForm.values.lookUpPhoneNumber,
                            )
                              ? alternateLookupForm.values.lookUpPhoneNumber
                              : ''
                          }
                          onChange={async (phoneEvent: any) => {
                            await onChangeInputHandle(
                              OutageLookUpType.Phone,
                              phoneEvent,
                            );
                          }}
                        />
                      ) : (
                        <AccountField
                          fieldRef={inputField}
                          error={
                            alternateLookupForm.values.lookUpType ===
                              OutageLookUpType.Account &&
                            Boolean(
                              alternateLookupForm.errors.lookUpAccountNumber,
                            )
                          }
                          helpertext={
                            alternateLookupForm.values.lookUpType ===
                              OutageLookUpType.Account &&
                            alternateLookupForm.errors.lookUpAccountNumber
                          }
                          value={
                            alternateLookupForm.values.lookUpType ===
                              OutageLookUpType.Account &&
                            Boolean(
                              alternateLookupForm.values.lookUpAccountNumber,
                            )
                              ? alternateLookupForm.values.lookUpAccountNumber
                              : ''
                          }
                          onChange={async (accountEvent: any) => {
                            await onChangeInputHandle(
                              OutageLookUpType.Account,
                              accountEvent,
                            );
                          }}
                        />
                      )}
                    </div>

                    {multiplePremiseInfo && (
                      <div style={{ marginLeft: '1rem' }}>
                        <MultiPremise
                          premiseList={multiplePremiseInfo}
                          onSelectPremise={handleMultiPremiseSelect}
                        />
                      </div>
                    )}
                  </Grid>
                )}
                {isAuthenticated && !isOtherLocation && (
                  <div className={classes.phoneNumberCheckBoxMobile}>
                    <OutageNotificationInfo isOtherLocation={isOtherLocation} />
                    <div
                      style={{
                        flex: '1.5',
                      }}
                    >
                      <CallbackNotification
                        onChange={(
                          checked: boolean,
                          phone: string,
                          error: any,
                        ) => {
                          setCallBackPhoneChecked(checked);
                          if (phone && phone !== '') {
                            setCallBackPhoneFieldValue(phone);
                          }
                        }}
                        primaryContactNumber={primaryContactNumber}
                        isFindAccount={true}
                      />
                    </div>
                  </div>
                )}
                <OutageButtonComponent
                  handleSubmit={handleSubmit}
                  disabled={reportedOutageButtonCheck()}
                />
                <BackButtonComponent
                  onPrevious={
                    isAuthenticated && !isOtherLocation ? onPrevious : onClose
                  }
                />
              </>
            )}
          </div>
        </BottomDrawerLayout>
      )}
    </>
  );
}

interface BackButtonComponentProps {
  onPrevious?: () => void;
}
interface OutageButtonComponentProps {
  handleSubmit: (e: any) => void;
  disabled: boolean;
}
interface SwitchDetailsProps {
  isOtherLocation: boolean;
  setIsMyAccount: (type: boolean) => void;
}
interface RenderAccountInfoProps {
  accountNumber: string;
  name: string;
  premiseInfo?: string;
}
interface OutageNotificationInfoProps {
  isOtherLocation: boolean;
}

interface RenderOtherLocationInfoProps {
  channelType: string;
  setChannelType: (e: any) => void;
  inputField: string;
  alternateLookupForm?: Model;
}
interface ChannelOptionsType {
  channelType: string;
  setChannelType: (e: any) => void;
}
interface AccountFieldType {
  fieldRef: MutableRefObject<undefined>;
  error: boolean;
  helpertext: false | FieldError;
  value: string;
  onChange: (e: any) => void;
}
interface PhoneFieldType {
  fieldRef: MutableRefObject<undefined>;
  error: boolean;
  helpertext: false | FieldError;
  value: string;
  onChange: (e: any) => void;
}

interface ShowReconnectPopupProps {
  onClose: () => void;
}

function SwitchDetails({
  isOtherLocation,
  setIsMyAccount,
}: SwitchDetailsProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const OutageToggleSwitch = styled(Switch)(({ theme }) => ({
    width: 30,
    height: 18,
    padding: 0,
    margin: '5px 14px',
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: 15,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: 'translateX(9px)',
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(12px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: 'white',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 14,
      height: 14,
      borderRadius: 6,
      backgroundColor: colors.sparkBlue,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
    },
    '& .MuiSwitch-track': {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: 'white',
      border: '1px solid gray',
      boxSizing: 'border-box',
    },
  }));
  return (
    <div
      className={
        !isMobile ? classes.switchDetailsStyle : classes.switchDetailsStyleMob
      }
    >
      <Typography variant={'subtitle2'}>{t('MY_ACCOUNT')}</Typography>{' '}
      <OutageToggleSwitch
        checked={isOtherLocation}
        inputProps={{ 'aria-label': 'Outage Toggle Switch' }}
        onChange={e => {
          setIsMyAccount(isOtherLocation);
        }}
      />
      <Typography variant={'subtitle2'}>{t('ANOTHER_LOCATION')}</Typography>{' '}
    </div>
  );
}

export function BackButtonComponent({ onPrevious }: BackButtonComponentProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <div className={classes.backButton}>
      <Button
        onClick={onPrevious}
        className={
          !isMobile ? classes.OutageBackButton : classes.OutageBackButtonMob
        }
      >
        <Typography>{t('BACK')}</Typography>
      </Button>
    </div>
  );
}

function OutageButtonComponent({
  handleSubmit,
  disabled,
}: OutageButtonComponentProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  return (
    <div className={classes.subButtonContainer}>
      <Button
        aria-label="checking-breaker-close"
        className={
          !isMobile ? classes.ReportOutageButton : classes.ReportOutageButtonMob
        }
        disabled={disabled}
        onClick={async e => await handleSubmit(e)}
      >
        {t('REPORT_OUTAGE')}
      </Button>
    </div>
  );
}

function RenderAccountInfo({
  accountNumber,
  name,
  premiseInfo,
}: RenderAccountInfoProps) {
  const { richT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  return (
    <div className={!isMobile ? '' : classes.RenderAccountInfoMob}>
      <Typography variant={'subtitle1'}>
        {richT('ACCOUNT_INFORMATION')}
      </Typography>
      <Typography>
        {accountNumber} {name && '-'} {name}
      </Typography>
      {premiseInfo?.split('\n').map(info => (
        <Typography>{info}</Typography>
      ))}
    </div>
  );
}

function ChannelOptions({ channelType, setChannelType }: ChannelOptionsType) {
  const { t } = useTranslation();
  const ChannelTypes = ['Phone', 'AccNo'];
  const getChannelMenuItems = () => {
    return ChannelTypes.map((item: string) => (
      <MenuItem value={item}>
        <Typography style={{ padding: '3px 5px 3px 14px' }}>
          {item === 'Phone' ? t('PHONE_LABEL') : t('ACCOUNT')}
        </Typography>
      </MenuItem>
    ));
  };
  return (
    <Select
      style={{
        width: '7rem',
        height: '42px',
        borderLeft: '1px solid gray',
        borderTop: '1px solid gray',
        marginTop: '16px',
      }}
      value={channelType}
      displayEmpty
      inputProps={{ 'aria-label': 'Without label' }}
      onChange={(e: any) => {
        setChannelType(e.target.value || '');
      }}
    >
      {getChannelMenuItems()}
    </Select>
  );
}

function AccountField({
  fieldRef,
  error,
  helpertext,
  value,
  onChange,
}: AccountFieldType) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  return (
    <div>
      <AccountTextField
        size={'small'}
        placeholder={t('ACCOUNT_NUMBER')}
        label={''}
        inputRef={fieldRef}
        data-testid={'account-number'}
        id="lookUpAccountNumber"
        autoComplete={t('ACCOUNT_NUMBER')}
        inputProps={{
          maxLength: 10,
          type: 'number',
          tabIndex: isMobile ? -1 : 0,
        }}
        error={error}
        helperText={helpertext}
        onChange={onChange}
        value={value}
        onFocus={onChange}
      />
    </div>
  );
}

function PhoneField({
  fieldRef,
  error,
  helpertext,
  value,
  onChange,
}: PhoneFieldType) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  return (
    <div>
      <PhoneTextField
        size={'small'}
        placeholder={t('PHONE_NUMBER')}
        label={''}
        inputRef={fieldRef}
        data-testid={'phone-number'}
        id="lookUpPhoneNumber"
        autoComplete={t('PHONE_NUMBER')}
        inputProps={{ tabIndex: isMobile ? -1 : 0 }}
        error={error}
        helperText={helpertext}
        value={value}
        onChange={onChange}
        onFocus={onChange}
      />
    </div>
  );
}

function OutageNotificationInfo({
  isOtherLocation,
}: OutageNotificationInfoProps) {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  return (
    <div
      className={
        !isMobile
          ? classes.outageNotificationInfo
          : classes.outageNotificationInfoMobile
      }
    >
      <div>
        <BulbIcon />
      </div>
      <div className={classes.notificationMessage}>
        <Typography variant={'subtitle1'}>
          {!isOtherLocation
            ? t('NOTIFY_TO_PHONENUMBER')
            : t('PHONE_NUMBER_NOTIFICATION')}
        </Typography>
      </div>
    </div>
  );
}

function ShowReconnectPopup({ onClose }: ShowReconnectPopupProps) {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t, richT } = useTranslation();
  const customerCareNumber = t('CUSTOMER_CARE_NUMBER_OTHER');

  return (
    <>
      {isMobile ? (
        <BottomDrawerLayout
          onClose={onClose}
          data-testid={'notification-options'}
          refId={0}
          defaultSnap={DrawerSnapType.default}
        >
          <ShowData onClose={onClose} />
        </BottomDrawerLayout>
      ) : (
        <Modal
          open={true}
          onClose={onClose}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
        >
          {<ShowData onClose={onClose} />}
        </Modal>
      )}
    </>
  );
}
const ShowData = ({ onClose }: ShowReconnectPopupProps) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t, richT } = useTranslation();
  const customerCareNumber = t('CUSTOMER_CARE_NUMBER_OTHER');
  return (
    <div
      className={
        !isMobile
          ? classes.reconnectPopupStyles
          : classes.reconnectPopupStylesMobile
      }
    >
      <div>
        <div className={classes.closeButton}>
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={onClose}
            data-testid="close-icon"
          >
            <Close />
          </IconButton>
        </div>
        <Typography variant={'h3'}>
          {t('RECONNECT_MESSAGE_TITLE_ANONYMOUS_FLOW')}
        </Typography>
        <div
          className={
            !isMobile
              ? classes.ReconnectAnonymousMessage
              : classes.ReconnectAnonymousMessageMobile
          }
        >
          <Typography variant={'subtitle1'}>
            {richT('RECONNECT_MESSAGE_ANONYMOUS_FLOW_CONTENT')}
          </Typography>
        </div>
        <div
          className={
            !isMobile
              ? classes.buttonContainerReconnectMessage
              : classes.buttonContainerReconnectMessageMobile
          }
        >
          <div className={classes.cancelButton}>
            <Button
              className={classes.reconnectAnonymousCloseButton}
              onClick={onClose}
              style={{
                background: colors.white,
                border: `1.5px solid ${colors.sparkBlue}`,
              }}
            >
              {t('CLOSE')}
            </Button>
          </div>
          <div className={classes.contactButton}>
            <Button
              aria-label="checking-breaker-close"
              className={classes.safetyPriorityCallButton}
              onClick={onClose}
            >
              <Typography>
                {
                  <a
                    href={'tel:' + customerCareNumber}
                    style={{
                      textDecoration: 'none',
                      color: colors.white,
                      fontSize: 'smaller',
                    }}
                  >
                    {' '}
                    {customerCareNumber}
                  </a>
                }
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
interface PremiseProp {
  houseNumber?: string;
  encryptedValue?: string;
}
interface MultiPremiseProps {
  premiseList: PremiseProp[];
  onSelectPremise: (premise: string) => void;
}
const MultiPremise = ({ premiseList, onSelectPremise }: MultiPremiseProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label={'report-outage-multiple-addresses'}
          name={'selectAddressForOutage'}
          onChange={async e => {
            await onSelectPremise(e.target.value);
          }}
        >
          <Grid
            container
            style={{ display: 'flex', justifyContent: 'center' }}
            direction={'column'}
          >
            <Typography
              variant={'body1'}
              style={{ paddingTop: 5 }}
              className={classes.selectLocationText}
            >
              {t('SELECT_HOUSE')}
            </Typography>
            {premiseList.map((account: PremiseProp, idx: number) => {
              return (
                <FormControlLabel
                  value={account?.encryptedValue}
                  control={
                    <Radio
                      color={'primary'}
                      className={classes.radioButtonIcon}
                    />
                  }
                  label={`${account?.houseNumber}`}
                  data-testid={`house-number-item-${idx}`}
                  key={`house-number-item-${idx}`}
                />
              );
            })}
          </Grid>
        </RadioGroup>
      </FormControl>
    </>
  );
};
